import { OvertimeRequest, User, DashboardStats } from '@/types';
import { OvertimeStatus, UserRole, TimeRange } from '@/types/enums';

/**
 * Centralized analytics utilities to eliminate redundant calculations
 * across the home page analytics components
 */

export interface DateRange {
  start: Date;
  end: Date;
}

export interface FilterOptions {
  departments?: string[];
  statuses?: string[];
  timeRange?: string;
}

export interface ProcessedOvertimeData {
  filteredRequests: OvertimeRequest[];
  currentMonthRequests: OvertimeRequest[];
  dashboardStats: DashboardStats | null;
  departmentData: DepartmentComparisonResult[];
  availableDepartments: string[];
  trends: {
    totalReqTrend: number;
    totalHoursTrend: number;
    approvalRateTrend: number;
    pendingTrend: number;
  };
}

export interface DepartmentComparisonResult {
  department: string;
  hours: number;
  perEmployee: number;
  employeeBreakdown: Array<{
    name: string;
    hours: number;
    employee_id?: string;
  }>;
}

/**
 * Create date ranges for different time periods
 */
export const createDateRanges = (timeRange: string = TimeRange.THIRTY_DAYS): DateRange => {
  const now = new Date();
  const start = new Date(now);
  let end = new Date(now);
  
  switch (timeRange) {
    case TimeRange.SEVEN_DAYS:
      start.setDate(now.getDate() - 7);
      break;
    case TimeRange.THIRTY_DAYS:
      start.setDate(now.getDate() - 30);
      break;
    case TimeRange.NINETY_DAYS:
      start.setDate(now.getDate() - 90);
      break;
    case TimeRange.THREE_SIXTY_FIVE_DAYS:
    case TimeRange.ONE_YEAR:
      // Last year - January 1st of last year to December 31st of last year
      start.setFullYear(now.getFullYear() - 1, 0, 1);
      end.setFullYear(now.getFullYear() - 1, 11, 31);
      break;
    case TimeRange.CALENDAR_MONTH:
      // Current calendar month - 1st to last day of current month
      start.setDate(1);
      end.setMonth(now.getMonth() + 1, 0); // Last day of current month
      break;
    case TimeRange.LAST_CALENDAR_MONTH:
      // Last calendar month - 1st to last day of previous month
      start.setMonth(now.getMonth() - 1, 1);
      end.setMonth(now.getMonth(), 0); // Last day of previous month
      break;
    case TimeRange.ALL:
      // All time - no date filtering
      start.setFullYear(2020, 0, 1); // Arbitrary start date
      end = new Date(now);
      break;
    default:
      start.setDate(now.getDate() - 30);
  }
  
  return { start, end };
};

/**
 * Get current month date range
 */
export const getCurrentMonthRange = (): DateRange => {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
  return { start, end };
};

/**
 * Filter overtime requests based on various criteria
 */
export const filterOvertimeRequests = (
  requests: OvertimeRequest[],
  filters: FilterOptions,
  user?: { role?: string; department?: string[] } | null
): OvertimeRequest[] => {
  if (!requests?.length) return [];

  return requests.filter(req => {
    // Date filtering
    if (filters.timeRange && filters.timeRange !== TimeRange.ALL) {
      const { start, end } = createDateRanges(filters.timeRange);
      const reqDate = new Date(req.date || req.submittedAt || req.submitted_at);
      if (isNaN(reqDate.getTime()) || reqDate < start || reqDate > end) {
        return false;
      }
    }

    // Department filtering
    if (filters.departments?.length && !filters.departments.includes(req.department || '')) {
      return false;
    }

    // Status filtering
    if (filters.statuses?.length) {
      const requestStatus = req.status || '';
      const isPendingStatus = requestStatus === OvertimeStatus.PENDING || 
                             requestStatus === OvertimeStatus.PENDING_VP_APPROVAL ||
                             requestStatus === OvertimeStatus.PENDING_DH_APPROVAL ||
                             requestStatus === OvertimeStatus.PENDING_PM_APPROVAL;
      
      // If OvertimeStatus.PENDING is in filters, include all pending-related statuses
      if (filters.statuses.includes(OvertimeStatus.PENDING) && isPendingStatus) {
        // Include this request
      } else if (!filters.statuses.includes(requestStatus)) {
        return false;
      }
    }

    // User role filtering - Only HR and super_admin see all departments
    if ((user?.role === UserRole.DEPARTMENT_HEAD || user?.role === UserRole.SUPERINTENDENT || user?.role === UserRole.PLANT_MANAGER) && user.department?.length) {
      return user.department.includes(req.department || '');
    }

    return true;
  });
};

/**
 * Filter requests for current month only
 */
export const filterCurrentMonthRequests = (
  requests: OvertimeRequest[],
  user?: { role?: string; department?: string[] } | null
): OvertimeRequest[] => {
  if (!requests?.length) return [];

  const { start, end } = getCurrentMonthRange();
  
  return requests.filter(req => {
    const reqDate = new Date(req.date || req.submittedAt || req.submitted_at);
    if (isNaN(reqDate.getTime())) return false;
    
    const isCurrentMonth = reqDate >= start && reqDate <= end;
    
    if ((user?.role === UserRole.DEPARTMENT_HEAD || user?.role === UserRole.SUPERINTENDENT || user?.role === UserRole.PLANT_MANAGER) && user.department?.length) {
      return isCurrentMonth && user.department.includes(req.department || '');
    }
    
    return isCurrentMonth;
  });
};

/**
 * Calculate dashboard statistics from overtime requests
 */
export const calculateDashboardStats = (
  requests: OvertimeRequest[],
  user?: { role?: string; department?: string[] } | null
): DashboardStats | null => {
  if (!requests?.length) return null;

  const currentMonthRequests = filterCurrentMonthRequests(requests, user);
  // Return 0 values instead of null when no data for department head
  if (!currentMonthRequests.length) {
    return {
      totalRequests: 0,
      pendingRequests: 0, // Will be 0 for all roles when no data
      approvedRequests: 0,
      rejectedRequests: 0,
      totalHours: 0,
      approvedHours: 0,
      weekOnWeekTrend: null
    };
  }

  const totalRequests = currentMonthRequests.length;
  
  // Role-specific pending requests calculation
  let pendingRequests = 0;
      if (user?.role === UserRole.HR) {
      // HR only sees superintendent requests with pending_dh_approval status
      pendingRequests = currentMonthRequests.filter(req => 
        req.status === OvertimeStatus.PENDING_DH_APPROVAL && 
        req.created_by_role?.toLowerCase() === UserRole.SUPERINTENDENT
      ).length;
    } else {
      // Other roles see all pending requests
      pendingRequests = currentMonthRequests.filter(req => 
        req.status === OvertimeStatus.PENDING || 
        req.status === OvertimeStatus.PENDING_VP_APPROVAL || 
        req.status === OvertimeStatus.PENDING_DH_APPROVAL
      ).length;
    }
  const approvedRequests = currentMonthRequests.filter(req => 
    req.status === OvertimeStatus.APPROVED
  ).length;
  const rejectedRequests = currentMonthRequests.filter(req => 
    req.status === OvertimeStatus.REJECTED
  ).length;
  
  const totalHours = currentMonthRequests
    .filter(req => req.status === OvertimeStatus.APPROVED)
    .reduce((sum, req) => sum + (req.hours || 0), 0);

  return {
    totalRequests,
    pendingRequests,
    approvedRequests,
    rejectedRequests,
    totalHours,
    approvedHours: totalHours,
    weekOnWeekTrend: null // Will be calculated separately
  };
};

/**
 * Calculate week-on-week trend
 */
export const calculateWeekOnWeekTrend = (requests: OvertimeRequest[]): number | null => {
  if (!requests?.length) return null;

  const now = new Date();
  const sevenDaysAgo = new Date(now.getTime() - (7 * 24 * 60 * 60 * 1000));
  const fourteenDaysAgo = new Date(now.getTime() - (14 * 24 * 60 * 60 * 1000));

  const thisWeekHours = requests
    .filter(req => {
      const reqDate = new Date(req.date || req.submittedAt || req.submitted_at);
      return !isNaN(reqDate.getTime()) && 
             reqDate >= sevenDaysAgo && 
             reqDate <= now && 
             req.status === OvertimeStatus.APPROVED;
    })
    .reduce((sum, req) => sum + (req.hours || 0), 0);

  const lastWeekHours = requests
    .filter(req => {
      const reqDate = new Date(req.date || req.submittedAt || req.submitted_at);
      return !isNaN(reqDate.getTime()) && 
             reqDate >= fourteenDaysAgo && 
             reqDate < sevenDaysAgo && 
             req.status === OvertimeStatus.APPROVED;
    })
    .reduce((sum, req) => sum + (req.hours || 0), 0);

  return lastWeekHours === 0 ? 0 : ((thisWeekHours - lastWeekHours) / lastWeekHours) * 100;
};

/**
 * Process department comparison data
 */
export const processDepartmentData = (
  requests: OvertimeRequest[],
  employees: User[]
): DepartmentComparisonResult[] => {
  if (!requests?.length || !employees?.length) return [];

  const approvedRequests = requests.filter(req => req.status === OvertimeStatus.APPROVED);
  if (!approvedRequests.length) return [];

  // Create employee lookup map
  const employeeMap = new Map(
    employees.map(emp => [emp.id, emp])
  );

  const departmentData = new Map<string, {
    hours: number;
    employees: Set<string>;
    employeeDetails: Map<string, { name: string; hours: number; employee_id?: string }>;
  }>();

  // Process approved requests
  for (const req of approvedRequests) {
    const dept = req.department || 'Unknown';
    const hours = req.hours || 0;
    
    if (!departmentData.has(dept)) {
      departmentData.set(dept, {
        hours: 0,
        employees: new Set(),
        employeeDetails: new Map()
      });
    }
    
    const deptData = departmentData.get(dept)!;
    deptData.hours += hours;
    
    // Employee lookup
    const employee = employeeMap.get(req.employeeId || req.employee_id);
    if (employee?.id) {
      deptData.employees.add(employee.id);
      const existing = deptData.employeeDetails.get(employee.id);
      deptData.employeeDetails.set(employee.id, {
        name: existing?.name || `${employee.firstName || ''} ${employee.lastName || ''}`.trim() || 'Unknown Employee',
        hours: (existing?.hours || 0) + hours,
        employee_id: employee.employee_id || employee.id
      });
    }
  }

  // Convert to array format
  return Array.from(departmentData.entries())
    .map(([dept, data]) => ({
      department: dept,
      hours: data.hours,
      perEmployee: data.employees.size > 0 ? data.hours / data.employees.size : 0,
      employeeBreakdown: Array.from(data.employeeDetails.values())
        .sort((a, b) => b.hours - a.hours)
    }))
    .sort((a, b) => b.hours - a.hours);
};

/**
 * Calculate trend metrics
 */
export const calculateTrends = (requests: OvertimeRequest[]) => {
  if (!requests?.length) {
    return {
      totalReqTrend: 0,
      totalHoursTrend: 0,
      approvalRateTrend: 0,
      pendingTrend: 0
    };
  }

  // Group by month
  const byMonth = new Map<string, { total: number; approved: number; hours: number; pending: number }>();
  
  for (const req of requests) {
    const d = new Date(req.date || req.submittedAt || req.submitted_at);
    if (isNaN(d.getTime())) continue;
    
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    if (!byMonth.has(key)) {
      byMonth.set(key, { total: 0, approved: 0, hours: 0, pending: 0 });
    }
    
    const entry = byMonth.get(key)!;
    entry.total += 1;
    
    if (req.status === OvertimeStatus.APPROVED) {
      entry.approved += 1;
      entry.hours += req.hours || 0;
    }
    
    if (req.status === OvertimeStatus.PENDING || req.status === OvertimeStatus.PENDING_VP_APPROVAL || req.status === OvertimeStatus.PENDING_DH_APPROVAL) {
      entry.pending += 1;
    }
  }

  // Current and previous month
  const now = new Date();
  const monthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const prevKey = `${prev.getFullYear()}-${String(prev.getMonth() + 1).padStart(2, '0')}`;

  const cur = byMonth.get(monthKey) || { total: 0, approved: 0, hours: 0, pending: 0 };
  const prevM = byMonth.get(prevKey) || { total: 0, approved: 0, hours: 0, pending: 0 };

  // Calculate trends
  const totalReqTrend = prevM.total > 0 ? ((cur.total - prevM.total) / prevM.total) * 100 : 0;
  
  // Calculate hours trend
  const totalHoursTrend = prevM.hours > 0 ? ((cur.hours - prevM.hours) / prevM.hours) * 100 : 0;
  
  const curRate = cur.total > 0 ? (cur.approved / cur.total) * 100 : 0;
  const prevRate = prevM.total > 0 ? (prevM.approved / prevM.total) * 100 : 0;
  const approvalRateTrend = prevRate !== 0 ? ((curRate - prevRate) / prevRate) * 100 : 0;

  // Pending day-over-day
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000);
  const isSameDay = (a: Date, b: Date) => 
    a.getFullYear() === b.getFullYear() && 
    a.getMonth() === b.getMonth() && 
    a.getDate() === b.getDate();
  
  let pendingToday = 0;
  let pendingYesterday = 0;
  
  for (const req of requests) {
    const d = new Date(req.date || req.submittedAt || req.submitted_at);
    if (isNaN(d.getTime())) continue;
    
    const isPending = req.status === OvertimeStatus.PENDING || req.status === OvertimeStatus.PENDING_VP_APPROVAL;
    if (!isPending) continue;
    
    if (isSameDay(d, today)) pendingToday += 1;
    else if (isSameDay(d, yesterday)) pendingYesterday += 1;
  }
  
  const pendingTrend = pendingYesterday > 0 ? ((pendingToday - pendingYesterday) / pendingYesterday) * 100 : 0;

  return {
    totalReqTrend,
    totalHoursTrend,
    approvalRateTrend,
    pendingTrend
  };
};

/**
 * Get available departments from filtered data
 */
export const getAvailableDepartments = (requests: OvertimeRequest[]): string[] => {
  const departments = new Set<string>();
  
  for (const req of requests) {
    if (req.department) {
      departments.add(req.department);
    }
  }
  
  return Array.from(departments).sort();
};

/**
 * Main function to process all analytics data
 */
export const processAnalyticsData = (
  requests: OvertimeRequest[],
  employees: User[],
  filters: FilterOptions,
  user?: { role?: string; department?: string[] } | null
): ProcessedOvertimeData => {
  const filteredRequests = filterOvertimeRequests(requests, filters, user);
  const currentMonthRequests = filterCurrentMonthRequests(requests, user);
  // Fix: Use filtered requests for dashboard stats when filters are applied
  const dashboardStats = filters.statuses?.length || filters.departments?.length 
    ? calculateDashboardStats(filteredRequests, user)
    : calculateDashboardStats(requests, user);
  const departmentData = processDepartmentData(filteredRequests, employees);
  const trends = calculateTrends(filteredRequests);

  // Add week-on-week trend to dashboard stats
  if (dashboardStats) {
    dashboardStats.weekOnWeekTrend = calculateWeekOnWeekTrend(currentMonthRequests);
  }

  return {
    filteredRequests,
    currentMonthRequests,
    dashboardStats,
    departmentData,
    availableDepartments: getAvailableDepartments(filteredRequests),
    trends
  };
};
